import * as React from 'react';
import { styled } from '@mui/material/styles';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import styles from './muiMenu.module.scss';
import {ReactNode} from "react";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    {...props}
  />
))(() => ({}));

export interface MenuItemData {
  label: string | ReactNode;
  icon?: React.ReactNode;
  onClick: (e?: any) => void;
  disabled?: boolean;
  divider?: boolean;
  style?: React.CSSProperties;
}

export interface CustomizedMenuProps {
  buttonText?: string;
  buttonVariant?: 'contained' | 'outlined' | 'text';
  menuItems: MenuItemData[];
  buttonId?: string;
  menuId?: string;
  showArrowIcon?: boolean;
  buttonStartIcon?: React.ReactNode;
  buttonEndIcon?: React.ReactNode;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  minWidth?: number;
  buttonStyle?: React.CSSProperties;
  disabled?: boolean;
}

const CustomizedMenu: React.FC<CustomizedMenuProps> = ({
  buttonText = 'Options',
  buttonVariant = 'contained',
  menuItems,
  buttonId,
  menuId,
  showArrowIcon = true,
  buttonStartIcon,
  buttonEndIcon,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  minWidth = 180,
  buttonStyle,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  // Lazy-mount the MUI <Menu> / Popper machinery. MUI's Menu is expensive to instantiate
  // (Popper + Modal + Transition layers) — on a 179-row table that's 179 hidden Poppers
  // built up-front, blocking the first paint of search updates. `hasOpened` flips to true
  // on the first click and stays true so the Menu can use its own close/open transitions.
  const [hasOpened, setHasOpened] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!hasOpened) setHasOpened(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => void, event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    onClick(event);
    handleClose();
  };

  const defaultButtonId = buttonId || 'customized-menu-button';
  const defaultMenuId = menuId || 'customized-menu';

  const buttonClassName = `${styles.menuButton} ${
    buttonVariant === 'outlined' ? styles.outlined : ''
  } ${buttonVariant === 'text' ? styles.text : ''}`;

  return (
    <div>
      <button
        id={defaultButtonId}
        aria-controls={open ? defaultMenuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        className={buttonClassName}
        onClick={handleClick}
        disabled={disabled}
        style={buttonStyle}
      >
        {buttonStartIcon && <span className={styles.buttonIcon}>{buttonStartIcon}</span>}
        <span>{buttonText}</span>
        {showArrowIcon && !buttonEndIcon && (
          <span className={styles.arrowIcon}>▼</span>
        )}
        {buttonEndIcon && <span className={styles.buttonIcon}>{buttonEndIcon}</span>}
      </button>
      {hasOpened && (
      <StyledMenu
        id={defaultMenuId}
        slotProps={{
          list: {
            'aria-labelledby': defaultButtonId,
            className: styles.menuList,
          },
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        marginThreshold={8}
        PaperProps={{
          className: styles.menuPaper,
          style: {
            minWidth: minWidth,
          },
        }}
        style={{ zIndex: 99999 }}
      >
        {menuItems.map((item, index) => (
            <MenuItem
              onClick={(event) => handleMenuItemClick(item.onClick, event)}
              disableRipple
              disabled={item.disabled}
              className={styles.menuItem}
              style={item.style}
            >
              {item.divider && index > 0 && (
                <Divider className={styles.menuDivider} />
              )}
              {item.icon && (
                <span className={styles.menuItemIcon}>{item.icon}</span>
              )}
              <span className={styles.menuItemLabel}>{item.label}</span>
            </MenuItem>
        ))}
      </StyledMenu>
      )}
    </div>
  );
};

export default CustomizedMenu;

